import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, resolveMediaUrl, primaryCategory, postCategories } from "../lib/api";
import { ArrowLeft, Microphone, Play, NewspaperClipping } from "@phosphor-icons/react";
import BodyRenderer, { toEmbed } from "../components/BodyRenderer";
import SEO from "../components/SEO";

const typeMeta = {
  article: { Icon: NewspaperClipping, label: "Article", accent: "#D4FF00" },
  podcast: { Icon: Microphone, label: "Podcast Episode", accent: "#E36A53" },
  digital: { Icon: Play, label: "Digital", accent: "#4FB7D7" },
};

function PodcastPlayer({ url }) {
  const embed = toEmbed(url);
  // If it's a Spotify/Apple/SoundCloud embed, render the iframe instead of <audio>.
  if (embed && embed.kind === "iframe") {
    const h = embed.aspect === "spotify-playlist" ? 380 : 232;
    return (
      <iframe
        data-testid="podcast-embed"
        src={embed.src}
        title="Podcast player"
        className="w-full border-0"
        style={{ height: h }}
        loading="lazy"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      />
    );
  }
  // Otherwise, native audio for uploaded files or direct mp3/wav URLs.
  return <audio data-testid="podcast-audio" src={resolveMediaUrl(url)} controls className="w-full" />;
}

function DigitalPlayer({ url, title }) {
  const embed = toEmbed(url);
  if (embed && embed.kind === "iframe") {
    const wrapperClass = embed.aspect === "spotify-audio"
      ? "h-[152px]"
      : embed.aspect === "spotify-playlist"
      ? "h-[380px]"
      : embed.aspect === "portrait"
      ? "aspect-[9/16] max-w-md mx-auto"
      : embed.aspect === "tweet"
      ? "min-h-[500px]"
      : "aspect-video";
    return (
      <div className={`${wrapperClass} w-full`}>
        <iframe
          data-testid="digital-embed"
          src={embed.src}
          title={title}
          className="h-full w-full"
          loading="lazy"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      </div>
    );
  }
  if (embed && embed.kind === "video") {
    return <video data-testid="digital-video" src={embed.src} controls className="w-full" />;
  }
  // Fallback: treat as video URL
  return (
    <div className="aspect-video w-full">
      <iframe
        data-testid="digital-embed"
        src={url}
        title={title}
        className="h-full w-full"
        loading="lazy"
        allowFullScreen
      />
    </div>
  );
}

function GalleryItem({ url, index }) {
  const resolved = resolveMediaUrl(url);
  const lower = url.toLowerCase();
  const isImage = /\.(jpe?g|png|gif|webp|avif)(\?|$)/.test(lower);
  const isVideoFile = /\.(mp4|webm|ogg|mov)(\?|$)/.test(lower);
  const isYouTube = /(youtube\.com|youtu\.be)/.test(lower);
  const isVimeo = /vimeo\.com/.test(lower);

  if (isImage) {
    return (
      <figure className="overflow-hidden border border-white/10 bg-black">
        <img src={resolved} alt={`Gallery item ${index + 1}`} className="h-auto w-full object-cover" />
      </figure>
    );
  }
  if (isVideoFile) {
    return (
      <figure className="overflow-hidden border border-white/10 bg-black">
        <video src={resolved} controls className="h-auto w-full" />
      </figure>
    );
  }
  if (isYouTube || isVimeo) {
    let embed = url;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
    if (ytMatch) embed = `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) embed = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return (
      <div className="aspect-video w-full overflow-hidden border border-white/10 bg-black">
        <iframe
          src={embed}
          title={`Gallery video ${index + 1}`}
          className="h-full w-full"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block border border-white/10 p-4 text-sm text-[#D4FF00] hover:border-[#D4FF00]">
      {url}
    </a>
  );
}

export default function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-32 text-center lg:px-12">
        <div className="font-display text-6xl font-bold tracking-tighter text-white">404</div>
        <p className="mt-4 text-sm uppercase tracking-[0.2em] text-white/60">Post not found</p>
        <Link to="/" className="mt-8 inline-block border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:border-[#D4FF00] hover:text-[#D4FF00]">Home</Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-[60vh]" />;

  const meta = typeMeta[post.type] || typeMeta.article;
  const Icon = meta.Icon;
  const date = new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <article data-testid="post-detail" className="bg-[#050505]">
      <SEO
        title={post.title}
        description={post.excerpt || post.body.slice(0, 160)}
        image={resolveMediaUrl(post.cover_image)}
        type="article"
        publishedAt={post.published_at}
        author={post.author}
        section={primaryCategory(post)}
        tags={post.tags || []}
      />
      {/* Cover */}
      <div className="relative h-[55vh] min-h-[320px] w-full overflow-hidden border-b border-white/10 sm:h-[60vh] sm:min-h-[400px]">
        <img src={post.cover_image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1400px] px-4 pb-8 sm:px-6 sm:pb-12 lg:px-12 lg:pb-20">
            <Link to="/" className="mb-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 hover:text-[#D4FF00] sm:mb-6 sm:text-xs">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] sm:text-xs" style={{ color: meta.accent }}>
              <Icon size={14} weight="bold" /> {meta.label} · {postCategories(post).join(" · ") || "General"}
            </div>
            <h1 className="mt-3 max-w-5xl font-display text-3xl font-bold leading-[0.95] tracking-tighter text-white sm:mt-4 sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-white/60 sm:mt-6 sm:gap-4 sm:text-xs">
              <span>{post.author}</span>
              <span className="h-1 w-1 bg-white/40" />
              <span>{date}</span>
              {post.duration && <><span className="h-1 w-1 bg-white/40" /><span>{post.duration}</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-10 sm:gap-12 sm:px-6 sm:py-16 lg:grid-cols-12 lg:px-12 lg:py-24">
        <div className="lg:col-span-8">
          {post.type === "podcast" && post.media_url && (
            <div className="mb-10 border border-white/10 bg-[#0a0a0a] p-6">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#E36A53]">Listen</div>
              <PodcastPlayer url={post.media_url} />
            </div>
          )}
          {post.type === "digital" && post.media_url && (
            <div className="mb-10 w-full overflow-hidden border border-white/10 bg-black">
              <DigitalPlayer url={post.media_url} title={post.title} />
            </div>
          )}
          {post.excerpt && <p className="mb-8 font-display text-2xl font-medium leading-tight tracking-tight text-white/90">{post.excerpt}</p>}
          <BodyRenderer body={post.body} />

          {post.gallery && post.gallery.length > 0 && (
            <div data-testid="post-gallery" className="mt-12 space-y-8 border-t border-white/10 pt-10">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Gallery</div>
              {post.gallery.map((url, i) => <GalleryItem key={`${i}:${url}`} url={url} index={i} />)}
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 border border-white/10 bg-[#0a0a0a] p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Tags</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="border border-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-white/80 hover:border-[#D4FF00] hover:text-[#D4FF00]"
                >
                  {t}
                </Link>
              ))}
            </div>
            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Written by</div>
              <div className="mt-2 font-display text-xl font-semibold text-white">{post.author}</div>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
